
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ChangeLocation from './ChangeLocation';
import { findCity, findCounty, findState } from '../utils/location'; 

export default function TagBar() {
  const nav = useNavigate();

  // Whatever the reducer currently has. Don’t destructure from {} – we want undefineds.
  const loc = useSelector(s => s.local?.location ?? {});
  const { state: st, county: ct, city: cy, label } = loc;

  // 1) Start with what Redux has
  let state = st || '';
  let county = ct || '';
  let city   = cy || '';

  // 2) If something’s missing, derive it from the label/constants
  //    Priority: label -> city -> county/state fallbacks
  const fromLabelCity = findCity(label);
  if (!city && fromLabelCity) city = fromLabelCity.name;

  if (!county) {
    county =
      fromLabelCity?.county ||
      (label ? findCounty(label)?.name : '') ||
      '';
  }

  if (!state) {
    state =
      fromLabelCity?.state ||
      (label ? findState(label)?.name : '') ||
      (county ? findCounty(county)?.state : '') ||
      '';
  }

  // 3) Remember last known city so city chip doesn’t disappear
  const lastCityRef = useRef(city || '');
  useEffect(() => { if (city) lastCityRef.current = city; }, [city]);
  const safeCity = city || lastCityRef.current || '';

  // Chip to auto-focus the currently active one
  const [activeKey, setActiveKey] = useState('city');

  // When label changes, decide which chip should be “active”
  useEffect(() => {
    if (label && label === state)  setActiveKey('state');
    else if (label && label === county) setActiveKey('county');
    else if (label && label === city)   setActiveKey('city');
    else if (safeCity) setActiveKey('city');
    else if (county)   setActiveKey('county');
    else if (state)    setActiveKey('state');
  }, [label, state, county, city, safeCity]);

  const refState  = useRef(null);
  const refCounty = useRef(null);
  const refCity   = useRef(null);

  // Smoothly focus the selected chip
  useEffect(() => {
    const map = { state: refState, county: refCounty, city: refCity };
    const el = map[activeKey]?.current;
    if (el) el.focus({ preventScroll: true });
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeKey]);

  const go = (key, value) => {
    if (!value) return;
    setActiveKey(key);
    nav(`/local/${encodeURIComponent(value)}`);
  };

  const Chip = useMemo(() => (props) => {
    const { innerRef, selected, onClick, children } = props;
    return (
      <button
        ref={innerRef}
        className={`tag ${selected ? 'active' : ''}`}
        aria-selected={selected}
        onClick={onClick}
      >
        {children}
      </button>
    );
  }, []);

  return (
    <div className="tag-bar d-flex gap-2 flex-wrap align-items-center">
      {state && (
        <Chip
          innerRef={refState}
          selected={activeKey === 'state'}
          onClick={() => go('state', state)}
        >
          {state}
        </Chip>
      )}

      {county && (
        <Chip
          innerRef={refCounty}
          selected={activeKey === 'county'}
          onClick={() => go('county', county)}
        >
          {county}
        </Chip>
      )}

      {safeCity && (
        <Chip
          innerRef={refCity}
          selected={activeKey === 'city'}
          onClick={() => go('city', safeCity)}
        >
          {safeCity}
        </Chip>
      )}

 
      <ChangeLocation currentCity={safeCity} asTag />
    </div>
  );
}

/*
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setLocation, fetchLocal } from "../content/local";
*/
/**
 * TagBar
 * - Restores old "Change Location" functionality via `onChangeLocation` prop
 * - Keeps quick-pick chips; override with `onPick` if you need custom logic
 *
 * Props:
 *  - chips?: string[]                // optional; otherwise derived from store
 *  - onPick?: (label: string) => void
 *  - onChangeLocation?: () => void   // your existing modal/picker opener
 *  - quickPicks?: boolean            // show chips row (default: true)
 */
/*
export default function TagBar({
  chips,
  onPick,
  onChangeLocation,
  quickPicks = true,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { city, county, state, label } =
    useSelector((s) => s.local?.location) || {};
  const current = (city || county || state || label || "California").trim();

  // Desktop: open; Mobile: collapsed (when we're using inline quick picks)
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 769px)");
    const sync = () => setOpen(mql.matches);
    sync();
    mql.addEventListener?.("change", sync);
    return () => mql.removeEventListener?.("change", sync);
  }, []);

  // Default quick-pick behavior (keeps your Redux + fetch + route)
  function defaultPick(nextLabel) {
    const value = String(nextLabel).trim();
    if (!value) return;
    dispatch(setLocation({ label: value }));
    dispatch(fetchLocal({ label: value }));
    navigate(`/local/${encodeURIComponent(value)}`, { replace: false });
  }

  const handlePick = onPick || defaultPick;

  const derived = [state || "California", county, city]
    .filter(Boolean)
    .map(String);
  const list = (chips?.length ? chips : derived).filter(Boolean);

  // Button click:
  // - If onChangeLocation exists, call it (restores your old modal/picker)
  // - Else toggle inline quick-picks (fallback)
  function handleChangeLocation() {
    if (typeof onChangeLocation === "function") {
      onChangeLocation();
    } else if (quickPicks) {
      setOpen((v) => !v);
    }
  }

  return (
    <div
      className={`tag-bar ${
        quickPicks ? (open ? "is-open" : "is-collapsed") : "no-quickpicks"
      }`}
    >
      <div className="tag-bar__row">
        <span className="chip chip--current" aria-label="Current location">
          {current}
        </span>

        <button
          type="button"
          className="tag-toggle"
          aria-expanded={quickPicks ? open : undefined}
          onClick={handleChangeLocation}
        >
          Change Location
        </button>
      </div>

      {quickPicks && (
        <div className="tag-bar__chips" hidden={!open}>
          {list.map((c) => (
            <button
              key={c}
              className="chip"
              onClick={() => {
                handlePick(c);
                // close after pick on mobile
                if (window.innerWidth < 769) setOpen(false);
              }}
            >
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
  */